import { useEffect, useRef, type ReactNode } from 'react';
import {
    BarController,
    BarElement,
    Chart,
    Filler,
    Interaction,
    Legend,
    LineController,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
    type ChartDataset,
    type ChartOptions,
    type InteractionItem,
    type InteractionModeFunction,
    type Plugin,
    type ScriptableContext,
} from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import type { Graph, TileOf } from '@casa/model';
import { formatDecimals, formatNumber } from '@casa/util';
import { withOpacity } from '../../elements/color.ts';
import { TILE_FONT_FAMILY } from '../../view/theme.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { barColor, graphSeries, tapTimeLabel, timeLabel, timeTicks, type GraphSeries } from './graph_tile.ts';

// The content area of a `graph` tile, drawn by Chart.js (docs/specs/graph-tile.md, the owner's
// choice after the spike): axes (GR-1), bars' values (GR-2), fills (GR-3), the value range (GR-4),
// grid lines (GR-5), a legend (GR-6) and the values at a tap (GR-7).

declare module 'chart.js' {
    interface InteractionModeMap {
        casaTime: InteractionModeFunction;
    }

    interface PluginOptionsByType<TType extends ChartType> {
        casaBarValues?: BarValuesOptions;
    }
}

type ChartType = import('chart.js').ChartType;
type Point = { x: number; y: number };

interface BarValuesOptions {
    display: boolean;
    format: (value: number) => string;
}

const AXIS_COLOR = '#9a9a9a';
const GRID_COLOR = 'rgba(255, 255, 255, 0.08)';
const FONT = { family: TILE_FONT_FAMILY, size: 11 };
/** Room above the bars for their values (GR-2), in CSS pixels. */
const BAR_VALUE_ROOM = 16;

/**
 * Each series' point nearest the tap in time, where the series has points on both sides of it, or
 * near (GR-7). Chart.js' own `index` mode pairs points by their place in the list, which differs
 * between series read at other times.
 */
const nearestInTime: InteractionModeFunction = (chart, event, _options, useFinalPosition) => {
    const { x } = getRelativePosition(event, chart as Chart);
    const items: InteractionItem[] = [];

    for (const [datasetIndex] of chart.data.datasets.entries()) {
        if (!chart.isDatasetVisible(datasetIndex)) {
            continue;
        }

        const elements = chart.getDatasetMeta(datasetIndex).data;
        const places = elements.map((element) => element.getProps(['x'], useFinalPosition).x as number);
        const first = places[0];
        const last = places.at(-1);

        if (first === undefined || last === undefined) {
            continue;
        }

        const reach = places.length > 1 ? (last - first) / (places.length - 1) : 0;

        if (x < first - reach || x > last + reach) {
            continue;
        }

        let index = 0;

        for (const [at, place] of places.entries()) {
            if (Math.abs(place - x) < Math.abs((places[index] ?? place) - x)) {
                index = at;
            }
        }

        const element = elements[index];

        if (element) {
            items.push({ element, datasetIndex, index });
        }
    }

    return items;
};

Interaction.modes.casaTime = nearestInTime;

/** Each bar's value above it (GR-2), where every one has room; none where they would run into each other. */
const barValues: Plugin<'bar' | 'line', BarValuesOptions> = {
    id: 'casaBarValues',

    afterDatasetsDraw(chart, _args, options) {
        if (!options.display) {
            return;
        }

        const { ctx } = chart;

        ctx.save();
        ctx.font = `${FONT.size}px ${FONT.family}`;

        const labels: { text: string; x: number; y: number }[] = [];
        let room = Infinity;

        for (const [index, dataset] of chart.data.datasets.entries()) {
            const meta = chart.getDatasetMeta(index);

            if (meta.type !== 'bar' || !chart.isDatasetVisible(index)) {
                continue;
            }

            for (const [at, element] of meta.data.entries()) {
                const point = dataset.data[at] as Point | undefined;
                const { x, y, width } = element.getProps(['x', 'y', 'width']) as { x: number; y: number; width: number };

                if (point) {
                    const text = options.format(point.y);
                    labels.push({ text, x, y });
                    room = Math.min(room, width + 2 - ctx.measureText(text).width);
                }
            }
        }

        if (room >= 0) {
            ctx.fillStyle = AXIS_COLOR;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';

            for (const { text, x, y } of labels) {
                ctx.fillText(text, x, y - 2);
            }
        }

        ctx.restore();
    },
};

Chart.register(LineController, BarController, LineElement, BarElement, PointElement, LinearScale, Filler, Legend, Tooltip);

/** The area under a line (GR-3): plain, or fading out to the bottom as the sparkline's (SP-10). */
function fillOf(each: GraphSeries) : Pick<ChartDataset<'line', Point[]>, 'fill' | 'backgroundColor'> {
    switch (each.fill) {
        case 'none':
            return { fill: false, backgroundColor: each.color };

        case 'solid':
            return { fill: 'start', backgroundColor: withOpacity(each.color, 0.4) };

        case 'fade':
            return {
                fill: 'start',
                backgroundColor: (context: ScriptableContext<'line'>) => {
                    const area = context.chart.chartArea;

                    if (!area) {
                        return 'transparent';
                    }

                    const gradient = context.chart.ctx.createLinearGradient(0, area.bottom, 0, area.top);

                    gradient.addColorStop(0, withOpacity(each.color, 0));
                    gradient.addColorStop(1, withOpacity(each.color, 0.5));

                    return gradient;
                },
            };
    }
}

function datasetOf(graph: Graph, each: GraphSeries) : ChartDataset<'line' | 'bar', Point[]> {
    const data = each.points.map((point) => ({ x: point.time + each.shift, y: point.value }));
    const label = each.label ?? '';

    if (graph.type === 'bar') {
        const colorOf = (context: ScriptableContext<'bar'>) : string => barColor((context.raw as Point | undefined)?.y ?? 0, each.color, each.thresholds);

        return { type: 'bar', label, data, backgroundColor: each.thresholds.length > 0 ? colorOf : each.color, borderWidth: 0 };
    }

    return {
        type: 'line',
        label,
        data,
        borderColor: each.color,
        // Device pixels, as in the legacy view.
        borderWidth: (context: ScriptableContext<'line'>) => each.lineWidth / (context.chart.currentDevicePixelRatio || 1),
        pointRadius: 0,
        pointHoverRadius: 3,
        pointBackgroundColor: each.color,
        tension: 0,
        ...fillOf(each),
    };
}

function optionsOf(graph: Graph, series: readonly GraphSeries[], locale: string | undefined) : ChartOptions<'line' | 'bar'> {
    const times = series.flatMap((each) => each.points.map((point) => point.time + each.shift));
    const first = Math.min(...times);
    const last = Math.max(...times);
    // Half a bar beside the first and last bars; Chart.js' own offset is half a tick step on a linear axis.
    const margin = graph.type === 'bar' ? barPitch(times) / 2 : 0;
    const format = (value: number) : string => formatNumber(value, graph.numberFormat, locale);
    const withUnit = (text: string) : string => (graph.unit ? `${text} ${graph.unit}` : text);
    const fixed = graph.min !== undefined && graph.max !== undefined && graph.min >= graph.max ? {} : { min: graph.min, max: graph.max };
    let step = 0;

    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        locale,
        interaction: { mode: 'casaTime', intersect: false },
        layout: {
            padding: {
                top: graph.barValues && graph.type === 'bar' ? BAR_VALUE_ROOM : 4,
                // Room for the lowest value's label, which the time axis' labels give otherwise.
                bottom: graph.timeAxis ? 0 : FONT.size / 2 + 1,
            },
        },
        scales: {
            x: {
                type: 'linear',
                bounds: 'data',
                offset: false,
                ...(times.length > 0 ? { min: first - margin, max: last + margin } : {}),
                // Grid lines at a shown axis' ticks (GR-5); a bar chart's would be shifted between them.
                grid: { display: graph.grid && graph.timeAxis, color: GRID_COLOR, offset: false },
                border: { display: graph.timeAxis, color: AXIS_COLOR },
                ticks: {
                    display: graph.timeAxis,
                    color: AXIS_COLOR,
                    maxRotation: 0,
                    autoSkip: false,
                    font: FONT,
                    callback: (value) => timeLabel(Number(value), step),
                },
                afterBuildTicks: (axis) => {
                    const built = timeTicks(axis.min, axis.max, axis.width);
                    step = built.step;
                    axis.ticks = built.ticks.map((value) => ({ value }));
                },
            },
            y: {
                type: 'linear',
                // The top never below 0, as in the legacy view, unless fixed (GR-4).
                suggestedMax: 0,
                ...fixed,
                grid: { display: graph.grid && graph.valueAxis, color: GRID_COLOR },
                border: { display: graph.valueAxis, color: AXIS_COLOR },
                ticks: {
                    display: graph.valueAxis,
                    color: AXIS_COLOR,
                    font: FONT,
                    maxTicksLimit: 6,
                    // The graph's number format, also here (the owner, 2026-10-29); steps rounded to its
                    // decimals, so that no two labels read the same.
                    precision: formatDecimals(graph.numberFormat),
                    callback: (value) => withUnit(format(Number(value))),
                },
            },
        },
        plugins: {
            legend: {
                display: graph.legend && series.length > 1 && series.some((each) => each.label),
                labels: { color: AXIS_COLOR, boxWidth: 12, font: FONT, filter: (item) => Boolean(item.text) },
            },
            tooltip: {
                titleFont: { family: TILE_FONT_FAMILY },
                bodyFont: { family: TILE_FONT_FAMILY },
                callbacks: {
                    title: (items) => (items[0] ? tapTimeLabel(items[0].parsed.x ?? 0, last - first) : ''),
                    label: (item) => {
                        const value = withUnit(format(item.parsed.y ?? 0));
                        return item.dataset.label ? `${item.dataset.label}: ${value}` : value;
                    },
                },
            },
            casaBarValues: { display: graph.barValues, format },
        },
    };
}

/** The shortest time between two readings, the room one bar takes; none with fewer than two. */
function barPitch(times: readonly number[]) : number {
    const sorted = [...new Set(times)].sort((a, b) => a - b);
    let pitch = Infinity;

    for (let at = 1; at < sorted.length; at++) {
        pitch = Math.min(pitch, (sorted[at] ?? 0) - (sorted[at - 1] ?? 0));
    }

    return Number.isFinite(pitch) ? pitch : 0;
}

/** The content area of a `graph` tile: the graph fills it. */
export function GraphTileView({ tile }: { tile: TileOf<'graph'> }) : ReactNode {
    const { graph } = tile.options;
    const environment = useTileEnvironment();
    const readings = environment.useReadingsList(tile, graph.series.map((series) => series.source));
    const series = graphSeries(graph, readings);
    const canvas = useRef<HTMLCanvasElement>(null);
    const chart = useRef<Chart<'line' | 'bar', Point[]> | null>(null);

    useEffect(() => {
        if (!canvas.current) {
            return;
        }

        chart.current = new Chart<'line' | 'bar', Point[]>(canvas.current, {
            type: graph.type,
            data: { datasets: [] },
            options: optionsOf(graph, [], environment.locale),
            plugins: [barValues],
        });

        return () => {
            chart.current?.destroy();
            chart.current = null;
        };
        // A new chart only for another kind; the next effect gives it its series and options.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graph.type]);

    useEffect(() => {
        const current = chart.current;

        if (!current) {
            return;
        }
        
        current.data.datasets = series.map((each) => datasetOf(graph, each));
        current.options = optionsOf(graph, series, environment.locale);
        current.update('none');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graph.type, JSON.stringify(graph), environment.locale, ...readings]);

    return (
        <div style={{ position: 'absolute', inset: 0 }}>
            <canvas ref={canvas} />
        </div>
    );
}
